import type { DecisionEngineJourneyDto } from '../api/admin';
import type { ApplicationTabKey } from './applicationDetailTabStatus';
import { LOAN_CATEGORY_LABELS } from './pdlAdjudicatorCopy';
import { buildPdlLegacyGridCalculation } from './pdlLegacyGridCalculation';

/**
 * Plain-language explanation of a payday (PDL) decline, written for someone who
 * does not know the policy: outcome → why → the customer's own numbers → what to
 * check. Insider detail (codes, tiers, ratios) goes to `technical`, shown collapsed.
 *
 * Mirrors svc-decision-engine pdl-affordability.policy.ts. Ratios are read from the
 * engine's PTI_TDS_CALC remark; the constants below are only the fallback defaults.
 */

/** DEFAULT_PDL_POLICY in svc-decision-engine. */
const DEFAULT_NEW_PAYMENT_PCT = 40;
const DEFAULT_ALL_DEBT_PCT = 50;
const PRODUCT_MIN = 100;
const PRODUCT_MAX = 1000;

export type PdlDeclineCalcRow = {
  label: string;
  value: string;
  /** minus = subtracted line; result = the bottom line; limit = the bar it is compared to. */
  kind?: 'minus' | 'result' | 'limit';
};

export type PdlDeclineExplanation = {
  headline: string;
  why: string;
  calculation: { rows: PdlDeclineCalcRow[]; verdict: string } | null;
  /** Extra plain lines, e.g. what the customer would have qualified for otherwise. */
  notes: string[];
  check: { label: string; tab: ApplicationTabKey } | null;
  technical: string[];
};

const fmt = (n: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n);

type Template = {
  headline: string;
  why: string;
  check?: { label: string; tab: ApplicationTabKey };
};

const CHECK_INCOME = { label: 'Open the Income tab', tab: 'income' } as const;
const CHECK_IDENTITY = { label: 'Open the Identity tab', tab: 'identity' } as const;
const CHECK_EMPLOYER = { label: 'Open the Employer tab', tab: 'employer' } as const;

/** One entry per decline code svc-decision-engine can return for payday. */
const TEMPLATES: Record<string, Template> = {
  ZERO_INCOME: {
    headline: 'Declined — no regular income was found in the customer’s bank account',
    why: 'We work out what a customer can afford from income paid into their connected bank account. No usable income was found.',
    check: CHECK_INCOME,
  },
  PDL_INCOME_MISSING: {
    headline: 'Declined — income information was missing when the decision ran',
    why: 'The income check had not produced a result when the payday decision ran, so affordability could not be worked out.',
    check: CHECK_INCOME,
  },
  JURISDICTION_BLOCKED: {
    headline: 'Declined — payday loans aren’t offered where the customer lives',
    why: 'Cash4You does not offer payday loans in the customer’s province or territory, whatever their income.',
    check: CHECK_IDENTITY,
  },
  FEDERAL_AMOUNT_EXCEEDED: {
    headline: 'Declined — the amount requested is above the legal payday limit',
    why: 'Federal rules cap the size of a payday loan, and the customer asked for more than that cap.',
  },
  ELOD_UNAVAILABLE: {
    headline: 'Declined — we couldn’t check the customer’s existing loans',
    why: 'The bank-account scan for existing loan payments did not run, so we cannot confirm the customer can afford another loan.',
    check: CHECK_INCOME,
  },
  INTERNAL_C4Y_LOAN: {
    headline: 'Declined — the customer is already repaying a Cash4You loan',
    why: 'Regular payments to Cash4You were found in the customer’s bank account. A new payday loan can’t be issued while one is still being repaid.',
  },
  PAYDAY_STACKING: {
    headline: 'Declined — the customer is repaying too many payday lenders',
    why: 'Payments to several different payday lenders were found in the customer’s bank account — more than our policy allows.',
  },
  OVER_TDS: {
    headline: 'Declined — the customer’s existing loan payments leave no room for another loan',
    why: 'After the loan payments the customer already makes, what is left is less than our smallest loan.',
  },
  UNDERAGE: {
    headline: 'Declined — the applicant is under 18',
    why: 'Applicants must be at least 18 years old.',
    check: CHECK_IDENTITY,
  },
  PRIOR_LOAN_PENDING: {
    headline: 'Declined — the customer’s previous payday loan hasn’t finished clearing',
    why: 'A new payday loan can’t start until the previous one has fully cleared.',
  },
  PRIOR_LOAN_NOT_CLEARED: {
    headline: 'Declined — the customer’s previous payday loan wasn’t repaid',
    why: 'Our records show the customer’s last payday loan did not clear.',
  },
  PAST_DUE: {
    headline: 'Declined — the customer has an overdue balance on a previous loan',
    why: 'Our loan records show money past due on an existing Cash4You loan.',
  },
  BLOCKED_INCOME_TYPE: {
    headline: 'Declined — this type of income isn’t eligible for a payday loan',
    why: 'Payday loans aren’t offered for some kinds of income, such as self-employed, commission or social-assistance income.',
    check: CHECK_INCOME,
  },
  BLOCKED_EMPLOYER: {
    headline: 'Declined — the customer’s employer isn’t eligible',
    why: 'The customer’s employer is on Cash4You’s list of employers we can’t lend against.',
    check: CHECK_EMPLOYER,
  },
  GRID_RULE_MISSING: {
    headline: 'Declined — no lending rule matched this customer',
    why: 'Our pre-approval rules have no entry for this customer’s mix of income and loan history, so no amount could be worked out.',
  },
};
// The admin copy has historically used UNDER_AGE; the engine emits UNDERAGE.
TEMPLATES.UNDER_AGE = TEMPLATES.UNDERAGE;

function readRatios(pdl: DecisionEngineJourneyDto['pdlAffordability']): {
  newPaymentPct: number;
  allDebtPct: number;
} {
  const msg = pdl?.remarks?.find((r) => r.code === 'PTI_TDS_CALC')?.message ?? '';
  const m = msg.match(/min\((\d+(?:\.\d+)?)% PTI,\s*(\d+(?:\.\d+)?)% TDS/i);
  return m
    ? { newPaymentPct: Number(m[1]), allDebtPct: Number(m[2]) }
    : { newPaymentPct: DEFAULT_NEW_PAYMENT_PCT, allDebtPct: DEFAULT_ALL_DEBT_PCT };
}

/** Engine principal ceiling from monthly room: whole dollars, $100 floor, $1,000 cap. */
function principalFromRoom(roomDollars: number): number {
  const dollars = Math.floor(roomDollars);
  return dollars < PRODUCT_MIN ? 0 : Math.min(PRODUCT_MAX, dollars);
}

function humanizeCode(code: string): string {
  const s = code.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function buildPdlDeclineExplanation(input: {
  decisionEngine?: DecisionEngineJourneyDto | null;
  applicationDeclineReason?: string | null;
}): PdlDeclineExplanation {
  const de = input.decisionEngine;
  const pdl = de?.pdlAffordability ?? null;
  const code = (input.applicationDeclineReason ?? de?.declinedReason ?? '').trim().toUpperCase();
  const legacy = buildPdlLegacyGridCalculation(pdl);
  const legacyLimit = legacy?.legacyLimit ?? null;
  const requested = de?.requestedAmount ?? null;

  const income = pdl?.monthlyIncome ?? de?.monthlyIncome ?? null;
  const existing = pdl?.existingMonthlyDebtServiceCents != null ? pdl.existingMonthlyDebtServiceCents / 100 : null;
  const roomFromEngine =
    pdl?.availableForNewPaymentCents != null ? pdl.availableForNewPaymentCents / 100 : null;
  const { newPaymentPct, allDebtPct } = readRatios(pdl);

  const template = TEMPLATES[code];
  let headline = template?.headline ?? 'Declined — the payday loan could not be offered';
  let why =
    template?.why ??
    (de?.declinedExplanation?.trim() || (code ? `${humanizeCode(code)}.` : 'The payday loan could not be offered.'));
  let check: PdlDeclineExplanation['check'] = template?.check ?? null;
  let calculation: PdlDeclineExplanation['calculation'] = null;
  const notes: string[] = [];

  if (code === 'PAYDAY_STACKING' && pdl?.paydayStackingCount) {
    why = `Payments to ${pdl.paydayStackingCount} different payday lenders were found in the customer’s bank account — more than our policy allows.`;
  }

  if (code === 'FEDERAL_AMOUNT_EXCEEDED' && requested != null && requested > 0) {
    why = `Federal rules cap the size of a payday loan, and the customer asked for ${fmt(requested)} — more than that cap.`;
  }

  // ── OVER_TDS: show the room calculation with the customer's own numbers ──────
  if (code === 'OVER_TDS' && income != null && income > 0) {
    const newPaymentCap = (income * newPaymentPct) / 100;
    const allDebtCap = (income * allDebtPct) / 100;
    const afterDebts = existing != null ? allDebtCap - existing : allDebtCap;
    const room = roomFromEngine ?? Math.max(0, Math.min(newPaymentCap, afterDebts));
    const roomShown = Math.max(0, room);

    if (legacyLimit != null && legacyLimit < PRODUCT_MIN && roomShown >= PRODUCT_MIN) {
      // Bound by the pre-approval table, not by debt or income.
      headline = 'Declined — the customer’s profile qualifies for less than our smallest loan';
      why = 'Our pre-approval rules for this customer’s income and loan history allow less than the smallest loan we offer.';
      calculation = {
        rows: [
          { label: 'Most this customer’s profile allows', value: fmt(legacyLimit) },
          { label: 'Smallest loan we offer', value: fmt(PRODUCT_MIN), kind: 'limit' },
        ],
        verdict: `${fmt(legacyLimit)} is below the ${fmt(PRODUCT_MIN)} minimum, so no loan can be offered.`,
      };
    } else if (existing != null && existing > 0 && afterDebts < newPaymentCap) {
      // Existing debt is what squeezes the room — the common case.
      calculation = {
        rows: [
          { label: 'Monthly income (verified from bank)', value: fmt(income) },
          { label: `Most we allow for all loan payments (${allDebtPct}% of income)`, value: fmt(allDebtCap) },
          { label: 'Already paying other lenders each month', value: `− ${fmt(existing)}`, kind: 'minus' },
          { label: 'Left for a new loan', value: fmt(roomShown), kind: 'result' },
          { label: 'Smallest loan we offer', value: fmt(PRODUCT_MIN), kind: 'limit' },
        ],
        verdict: `${fmt(roomShown)} is less than the ${fmt(PRODUCT_MIN)} minimum, so no loan can be offered.`,
      };
      notes.push('The loans behind this figure are listed under “Existing debts (ELOD)” below.');
      const withoutDebts = principalFromRoom(Math.min(newPaymentCap, allDebtCap));
      const qualify = legacyLimit != null ? Math.min(legacyLimit, withoutDebts) : withoutDebts;
      if (qualify >= PRODUCT_MIN) {
        notes.push(`Without these existing loan payments, the customer would have qualified for up to ${fmt(qualify)}.`);
      }
    } else {
      // Income alone is too small for the minimum loan.
      headline = 'Declined — the customer’s income is too low for our smallest loan';
      why = 'Even before any other debts, the share of income we allow for a loan payment is less than our smallest loan.';
      calculation = {
        rows: [
          { label: 'Monthly income (verified from bank)', value: fmt(income) },
          { label: `Most we allow for one loan payment (${newPaymentPct}% of income)`, value: fmt(newPaymentCap), kind: 'result' },
          { label: 'Smallest loan we offer', value: fmt(PRODUCT_MIN), kind: 'limit' },
        ],
        verdict: `${fmt(Math.floor(newPaymentCap))} is less than the ${fmt(PRODUCT_MIN)} minimum, so no loan can be offered.`,
      };
      check = CHECK_INCOME;
    }
  }

  // Bank-detected stops: say what the customer would otherwise have qualified for.
  if ((code === 'INTERNAL_C4Y_LOAN' || code === 'PAYDAY_STACKING') && roomFromEngine != null) {
    notes.push('The loans found are listed under “Existing debts (ELOD)” below.');
    const afford = principalFromRoom(roomFromEngine);
    const qualify = legacyLimit != null ? Math.min(legacyLimit, afford) : afford;
    if (qualify >= PRODUCT_MIN) {
      notes.push(`If it weren’t for this, the customer would have qualified for up to ${fmt(qualify)}.`);
    }
  }

  if (requested != null && requested > 0) {
    notes.push(`The customer asked for ${fmt(requested)}. Amount offered: ${fmt(0)}.`);
  }

  // ── Technical details (collapsed): everything an insider needs, nothing an outsider must read ──
  const technical: string[] = [];
  if (code) technical.push(`Decision code: ${code}`);
  if (de?.declinedExplanation?.trim()) technical.push(`Engine explanation: ${de.declinedExplanation.trim()}`);
  if (income != null && (roomFromEngine != null || existing != null)) {
    technical.push(
      `Capacity: room = min(${newPaymentPct}% PTI, ${allDebtPct}% TDS − existing debt)` +
        (roomFromEngine != null ? ` = ${fmt(roomFromEngine)}/mo` : '') +
        (existing != null ? `; ELOD existing debt ${fmt(existing)}/mo` : '') +
        (pdl?.debtServiceRatio != null ? `, DSR ${(pdl.debtServiceRatio * 100).toFixed(1)}%` : ''),
    );
  }
  if (legacy?.customerTier != null) {
    const label = LOAN_CATEGORY_LABELS[legacy.customerTier] ?? `Tier ${legacy.customerTier}`;
    technical.push(`Servicing tier: ${label} (legacy pre-approval table only — separate from bank ELOD)`);
  }
  if (legacyLimit != null) {
    technical.push(
      `Pre-approval table${legacy?.scoringTable != null ? ` #${legacy.scoringTable}` : ''} limit before stop: ${fmt(legacyLimit)}`,
    );
  }
  if (pdl?.paydayStackingCount != null) technical.push(`Payday stacking count: ${pdl.paydayStackingCount}`);

  return { headline, why, calculation, notes, check, technical };
}
